import {
  Box,
  Button,
  Flex,
  Heading,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast
} from '@chakra-ui/react'
import { CreditCardForm } from '@handlers/settings/update-your-plan/components'
import LockIcon from '@material-ui/icons/Lock'
import {
  CardNumberElement,
  Elements,
  useElements,
  useStripe
} from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { useStoreState } from 'easy-peasy'
import React, { useRef, useState } from 'react'
import { useQueryClient } from 'react-query'
import { Link as RouterLink, useHistory } from 'react-router-dom'
import {
  getOrganizationControllerGetSessionCreditQueryKey,
  useClinicControllerGetPaymentMethods,
  useOrganizationControllerGetSessionCredit,
  useOrganizationControllerSessionCreditsPaymentMethod,
  useOrganizationControllerUpdateBillingEmail
} from '~/clinician-api'
import { AutoReplenish } from '../components/auto-replenish'
import { InvoicesTable } from '../components/invoices-table'

// @ts-ignore
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY)

const PLANS = {
  free: 'Starter',
  standard: 'Standard',
  plus: 'Plus',
  pro: 'Pro'
}

const PLAN_PRICE = {
  free: '5 free sessions',
  standard: '$0.49 per session',
  plus: '$0.99 per session',
  pro: '$1.49 per session'
}

const PaymentMethodModal = ({
  isOpen,
  onClose,
  onSavePaymentMethod
}: {
  isOpen: boolean
  onClose: () => void
  onSavePaymentMethod: () => void
}) => {
  const [isPaymentMethodComplete, setIsPaymentMethodComplete] = useState(false)
  const initialRef = useRef<HTMLDivElement>(null)
  const finalRef = useRef<HTMLDivElement>(null)

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      initialFocusRef={initialRef}
      finalFocusRef={finalRef}
    >
      <ModalOverlay />
      <ModalContent>
        <div ref={initialRef} tabIndex={-1} style={{ outline: 'none' }} />
        <div
          ref={finalRef}
          tabIndex={-1}
          style={{ outline: 'none', position: 'absolute', left: '-9999px' }}
        />
        <ModalHeader fontSize="20px">Enter Credit or Debit Card</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Box mb="medium">
            <Flex w="100%" justify="space-between" align="center" mb="medium">
              <Text>Enter your payment details</Text>
              <Flex gap="small">
                <LockIcon fontSize="small" />
                <Text>Secure form</Text>
              </Flex>
            </Flex>
            <CreditCardForm
              onComplete={(isComplete: boolean) => {
                setIsPaymentMethodComplete(isComplete)
              }}
            />
          </Box>
          <Flex justify="end" gap="small">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={onSavePaymentMethod}
              isDisabled={!isPaymentMethodComplete}
            >
              Use this card
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

const BillingEmailModal = ({
  isOpen,
  onClose,
  onSaveBillingEmail
}: {
  isOpen: boolean
  onClose: () => void
  onSaveBillingEmail: (email: string) => void
}) => {
  const [email, setEmail] = useState('')
  const initialRef = useRef<HTMLDivElement>(null)
  const finalRef = useRef<HTMLDivElement>(null)

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      initialFocusRef={initialRef}
      finalFocusRef={finalRef}
    >
      <ModalOverlay />
      <ModalContent>
        <div ref={initialRef} tabIndex={-1} style={{ outline: 'none' }} />
        <div
          ref={finalRef}
          tabIndex={-1}
          style={{ outline: 'none', position: 'absolute', left: '-9999px' }}
        />
        <ModalHeader fontSize="20px">Change Billing Contact</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Box mb="medium">
            <Text mb="small">
              The email address we'll use to send invoices and payment
              confirmations.
            </Text>
            <Input
              type="email"
              placeholder="Email"
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
          </Box>
          <Flex justify="end" gap="small">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={() => onSaveBillingEmail(email)}
              isDisabled={!email}
            >
              Update
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

const PlanContent = () => {
  const history = useHistory()
  const { user } = useStoreState((state: any) => state.auth)
  const toast = useToast()
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false)
  const [isBillingEmailModalOpen, setIsBillingEmailModalOpen] = useState(false)
  const stripe = useStripe()
  const elements = useElements()
  const queryClient = useQueryClient()

  const { mutate: savePaymentMethod } =
    useOrganizationControllerSessionCreditsPaymentMethod({
      mutation: {
        onSuccess: () => {
          toast({
            title: 'Payment method added successfully',
            status: 'success',
            isClosable: true,
            duration: 3000
          })
          setIsPaymentModalOpen(false)
          refetchPaymentMethods()
        },
        onError: () => {
          toast({
            title: 'Error adding payment method',
            status: 'error',
            isClosable: true,
            duration: 3000
          })
        }
      }
    })

  const { mutate: updateBillingEmail } =
    useOrganizationControllerUpdateBillingEmail({
      mutation: {
        onSuccess: () => {
          toast({
            title: 'Billing email updated successfully',
            status: 'success',
            isClosable: true,
            duration: 3000
          })
          setIsBillingEmailModalOpen(false)
          queryClient.invalidateQueries(
            getOrganizationControllerGetSessionCreditQueryKey(
              user?.clinic?.organization_id
            )
          )
        },
        onError: () => {
          toast({
            title: 'Error updating billing email',
            status: 'error',
            isClosable: true,
            duration: 3000
          })
        }
      }
    })

  const { data: sessionCredits } = useOrganizationControllerGetSessionCredit(
    user?.clinic?.organization_id,
    {
      query: {
        enabled: !!user?.clinic?.organization_id
      }
    }
  )

  const { data: paymentMethods = [], refetch: refetchPaymentMethods } =
    useClinicControllerGetPaymentMethods(user?.clinic?.id || '', {
      query: {
        enabled: !!user?.clinic?.id
      }
    })

  const defaultPaymentMethod = paymentMethods.find(p => p.isDefault)

  const handleSavePaymentMethod = async () => {
    const cardElement = elements?.getElement(CardNumberElement)

    if (!cardElement) {
      console.error('Card Element not available')
      return
    }

    const paymentMethod = await stripe?.createPaymentMethod({
      type: 'card',
      card: cardElement
    })
    await savePaymentMethod({
      organizationId: user?.clinic?.organization_id,
      data: {
        paymentMethodId: paymentMethod?.paymentMethod?.id || '',
        stripeCustomerId: user?.clinic?.stripe_customer_id
      }
    })
  }

  const handleSaveBillingEmail = async (email: string) => {
    await updateBillingEmail({
      organizationId: user?.clinic?.organization_id,
      data: {
        billingEmail: email
      }
    })
  }

  return (
    <Box>
      <Box mb={{ base: 'medium', md: 'large' }} w={'100%'}>
        <Heading mb="xxsmall" fontSize={{ base: '24px', md: '32px' }}>
          Plan
        </Heading>
        <Text fontSize={{ base: '14px', md: '16px' }}>
          Manage your sessions and billing preferences
        </Text>
      </Box>
      {/* Plan / Sessions */}
      <Flex
        width="100%"
        direction={{ base: 'column', md: 'row' }}
        justify="space-between"
        gap={{ base: 'medium', md: 'large' }}
        mb="xlarge"
      >
        <Box w={{ base: '100%', md: '50%' }}>
          <Flex justify="space-between" align="center" mb="small">
            <Text fontWeight="bold">Current Plan</Text>
            <Button
              variant="outline"
              borderRadius="8px"
              onClick={() => history.push('/settings/plan/manage')}
              size="sm"
            >
              {sessionCredits?.planType !== 'free'
                ? 'Change Plan'
                : 'Choose Plan'}
            </Button>
          </Flex>
          <Box
            p="medium"
            border="1px"
            borderColor="pale_gray"
            borderRadius="8px"
          >
            <Text fontWeight="bold">
              {PLANS[sessionCredits?.planType as keyof typeof PLANS]}
            </Text>
            <Text>
              {PLAN_PRICE[sessionCredits?.planType as keyof typeof PLAN_PRICE]}
            </Text>
          </Box>
        </Box>
        <Box w={{ base: '100%', md: '50%' }}>
          <Flex justify="space-between" align="center" mb="small">
            <Text fontWeight="bold">Sessions</Text>
            <Button
              variant="outline"
              borderRadius="8px"
              onClick={() => history.push('/settings/plan/manage')}
              size="sm"
            >
              Purchase Sessions
            </Button>
          </Flex>
          <Box
            p="medium"
            border="1px"
            borderColor="pale_gray"
            borderRadius="8px"
          >
            <Text fontWeight="bold">
              {(sessionCredits?.freeCreditsRemaining || 0) +
                (sessionCredits?.creditsRemaining || 0)}
            </Text>
            <Text>sessions left</Text>
          </Box>
        </Box>
      </Flex>
      {/* Auto Replenish */}
      {sessionCredits?.planType !== 'free' && (
        <Box mb="xlarge">
          <AutoReplenish
            propAutoReplenishEnabled={
              sessionCredits?.autoReplenishEnabled ?? false
            }
            propAutoReplenishThreshold={
              sessionCredits?.autoReplenishThreshold ?? null
            }
            propAutoReplenishCount={sessionCredits?.autoReplenishCount ?? null}
          />
        </Box>
      )}
      {/* Payment Method / Billing Email */}
      <Flex
        width="100%"
        direction={{ base: 'column', md: 'row' }}
        justify="space-between"
        gap={{ base: 'medium', md: 'large' }}
        mb="xlarge"
      >
        <Box w={{ base: '100%', md: '50%' }}>
          <Flex justify="space-between" align="center" mb="small">
            <Text fontWeight="bold">Payment Method</Text>
            <Button
              variant="outline"
              borderRadius="8px"
              onClick={() => setIsPaymentModalOpen(true)}
              size="sm"
            >
              {defaultPaymentMethod ? 'Update Payment' : 'Add Payment'}
            </Button>
          </Flex>
          <Box
            p="medium"
            border="1px"
            borderColor="pale_gray"
            borderRadius="8px"
          >
            {defaultPaymentMethod ? (
              <>
                <Text
                  fontWeight="bold"
                  fontSize={{ base: '14px', md: '16px' }}
                  noOfLines={1}
                >{`${defaultPaymentMethod.brand.charAt(0).toUpperCase() + defaultPaymentMethod.brand.slice(1)} ending in ****${defaultPaymentMethod.lastFourDigits}`}</Text>
                <Text>{`Expires ${defaultPaymentMethod.expMonth}/${defaultPaymentMethod.expYear.toString().slice(2)}`}</Text>
              </>
            ) : (
              <>
                <Text fontWeight="bold">No card on file</Text>
                <Text>Add payment method</Text>
              </>
            )}
          </Box>
        </Box>
        <Box w={{ base: '100%', md: '50%' }}>
          <Flex justify="space-between" align="center" mb="small">
            <Text fontWeight="bold">Billing Information</Text>
            <Button
              variant="outline"
              borderRadius="8px"
              onClick={() => setIsBillingEmailModalOpen(true)}
              size="sm"
            >
              Update Contact
            </Button>
          </Flex>
          <Box
            p="medium"
            border="1px"
            borderColor="pale_gray"
            borderRadius="8px"
          >
            <Text fontWeight="bold">Invoices sent to:</Text>
            <Text>{sessionCredits?.billingEmail || user?.email}</Text>
          </Box>
        </Box>
      </Flex>
      <PaymentMethodModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        onSavePaymentMethod={handleSavePaymentMethod}
      />
      <BillingEmailModal
        isOpen={isBillingEmailModalOpen}
        onClose={() => setIsBillingEmailModalOpen(false)}
        onSaveBillingEmail={handleSaveBillingEmail}
      />
      {/* Invoices Table */}
      <Box mb="xlarge">
        <Text fontWeight="bold" mb="small">
          Invoices
        </Text>
        <InvoicesTable organizationId={user?.clinic?.organization_id} />
      </Box>
      <Box
        w="100%"
        border="1px solid"
        borderColor="#E4E5E6"
        borderRadius="8px"
        mt={'40px'}
        mb={'64px'}
        bg="white"
        p={'medium'}
        verticalAlign={'middle'}
      >
        <Text fontSize={'16px'} fontWeight={'bold'} mb={'6px'}>
          Want to permanently delete your organization?
        </Text>
        <Text fontSize={'14px'} color={'gray.600'} mb={'6px'}>
          All of your clinic's data will be erased and all user accounts
          associated with your organization will be deleted.
        </Text>
        <Link as={RouterLink} color="#EB5164" to="/delete">
          Delete your organization
        </Link>
      </Box>
    </Box>
  )
}

export const Plan = () => {
  const options = {
    fonts: [
      {
        family: 'TofinoPersonal',
        src: 'url(https://res.cloudinary.com/hellojoy/raw/upload/v1631114007/Fonts/tofino-personal/TofinoPersonal-Regular.otf)'
      }
    ]
  }

  return (
    <Elements stripe={stripePromise} options={options}>
      <PlanContent />
    </Elements>
  )
}
